import React, { forwardRef } from 'react';
import * as ProgressPrimitive from '@radix-ui/react-progress';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../theme';

// ============================================================================
// PROGRESS VARIANTS
// ============================================================================

const progressVariants = cva(
  'relative w-full overflow-hidden rounded-full bg-cream-dark',
  {
    variants: {
      size: {
        sm: 'h-1.5',
        md: 'h-2.5',
        lg: 'h-4',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

const progressIndicatorVariants = cva(
  'h-full w-full flex-1 transition-all duration-300 ease-out',
  {
    variants: {
      variant: {
        default: 'bg-sage',
        success: 'bg-success',
        warning: 'bg-terracotta',
        danger: 'bg-red-500',
        info: 'bg-blue-500',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

// ============================================================================
// PROGRESS
// ============================================================================

export interface ProgressProps
  extends React.ComponentPropsWithoutRef<typeof ProgressPrimitive.Root>,
    VariantProps<typeof progressVariants>,
    VariantProps<typeof progressIndicatorVariants> {
  /**
   * Whether to show the percentage label.
   */
  showLabel?: boolean;
  /**
   * Custom label format function.
   */
  formatLabel?: (value: number) => string;
}

/**
 * Progress bar component for showing completion status.
 * 
 * @example
 * // Basic progress
 * <Progress value={60} />
 * 
 * // With label
 * <Progress value={75} showLabel />
 * 
 * // Custom variant and size
 * <Progress value={100} variant="success" size="lg" />
 */
const Progress = forwardRef<
  React.ElementRef<typeof ProgressPrimitive.Root>,
  ProgressProps
>(({ 
  className, 
  value = 0, 
  size, 
  variant,
  showLabel = false,
  formatLabel = (v) => `${Math.round(v)}%`,
  ...props 
}, ref) => {
  const clampedValue = Math.max(0, Math.min(100, value ?? 0));

  return (
    <div className="w-full">
      {showLabel && (
        <div className="flex justify-between mb-1.5">
          <span className="text-sm font-medium text-navy">Progress</span>
          <span className="text-sm font-medium text-sage">
            {formatLabel(clampedValue)}
          </span>
        </div>
      )}
      <ProgressPrimitive.Root
        ref={ref}
        className={cn(progressVariants({ size }), className)}
        value={clampedValue}
        {...props}
      >
        <ProgressPrimitive.Indicator
          className={cn(progressIndicatorVariants({ variant }))}
          style={{ transform: `translateX(-${100 - clampedValue}%)` }}
        />
      </ProgressPrimitive.Root>
    </div>
  );
});

Progress.displayName = ProgressPrimitive.Root.displayName;

// ============================================================================
// PROGRESS CIRCLE
// ============================================================================

interface ProgressCircleProps extends React.SVGAttributes<SVGSVGElement> {
  value?: number;
  size?: 'sm' | 'md' | 'lg';
  strokeWidth?: number;
  showLabel?: boolean;
  variant?: 'default' | 'success' | 'warning' | 'danger';
}

const sizeMap = {
  sm: 40,
  md: 64,
  lg: 96,
};

const strokeColors = {
  default: '#7A9E7E',
  success: '#4CAF9E',
  warning: '#C4785A',
  danger: '#E57373',
};

/**
 * Circular progress indicator.
 */
function ProgressCircle({
  value = 0,
  size = 'md',
  strokeWidth = 4,
  showLabel = true,
  variant = 'default',
  className,
  ...props
}: ProgressCircleProps) {
  const dimension = sizeMap[size];
  const radius = (dimension - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const clampedValue = Math.max(0, Math.min(100, value));
  const strokeDashoffset = circumference - (clampedValue / 100) * circumference;

  return (
    <div className={cn('relative inline-flex items-center justify-center', className)}>
      <svg
        width={dimension}
        height={dimension}
        viewBox={`0 0 ${dimension} ${dimension}`}
        {...props}
      >
        {/* Background circle */}
        <circle
          cx={dimension / 2}
          cy={dimension / 2}
          r={radius}
          fill="none"
          stroke="#F5EDE4"
          strokeWidth={strokeWidth}
        />
        {/* Progress circle */}
        <circle
          cx={dimension / 2}
          cy={dimension / 2}
          r={radius}
          fill="none"
          stroke={strokeColors[variant]}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          transform={`rotate(-90 ${dimension / 2} ${dimension / 2})`}
          style={{ transition: 'stroke-dashoffset 0.3s ease-out' }}
        />
      </svg>
      {showLabel && (
        <span
          className={cn(
            'absolute font-semibold',
            size === 'sm' && 'text-xs',
            size === 'md' && 'text-sm',
            size === 'lg' && 'text-lg'
          )}
          style={{ color: strokeColors[variant] }}
        >
          {Math.round(clampedValue)}%
        </span>
      )}
    </div>
  );
}

// ============================================================================
// EXPORTS
// ============================================================================

export { Progress, ProgressCircle, progressVariants, progressIndicatorVariants };
